import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Award, Users, Smile } from 'lucide-react';

const Stats = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    {
      icon: <Award className="w-12 h-12" />,
      number: 1000,
      suffix: '+',
      label: 'Events Completed',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: <Smile className="w-12 h-12" />,
      number: 500,
      suffix: '+',
      label: 'Happy Clients',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: <Users className="w-12 h-12" />,
      number: 50,
      suffix: '+',
      label: 'Team Members',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  // Intersection Observer to detect when section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Counter Component
  const Counter = ({ target, suffix, duration = 2000 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;

      let startTime = null;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / duration;

        if (progress < 1) {
          setCount(Math.floor(target * progress));
          requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };

      requestAnimationFrame(animate);
    }, [isVisible, target, duration]);

    return (
      <span>
        {count}{suffix}
      </span>
    );
  };

  return (
    <section 
      ref={sectionRef}
      className="py-16 bg-gradient-to-br from-orange-50 to-white relative overflow-hidden"
    >
      {/* Background Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-20 -z-10"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#b8d4d4] rounded-full blur-3xl opacity-20 -z-10"></div>

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 p-8 text-center border-2 border-transparent hover:border-orange-300"
            >
              {/* Icon with gradient background */}
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${stat.color} text-white mb-6 shadow-lg group-hover:scale-110 transition-transform duration-500`}>
                {stat.icon}
              </div>

              {/* Number with Animation */}
              <h3 
                className="text-5xl font-black text-gray-900 mb-3 group-hover:text-orange-600 transition-colors duration-300" 
                style={{ fontFamily: "'Diplomata', 'Arbutus', cursive" }}
              >
                <Counter target={stat.number} suffix={stat.suffix} duration={2000} />
              </h3>

              {/* Label */}
              <p 
                className="text-lg text-gray-600 font-semibold" 
                style={{ fontFamily: "'Hepta Slab', serif" }}
              >
                {stat.label}
              </p>

              {/* Decorative line */}
              <div className="w-16 h-1 bg-gradient-to-r from-orange-500 to-[#b8d4d4] mx-auto mt-4 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;